import PrimaryCTA from "components/buttons/PrimaryCTA"
import gsap from "gsap"
import DrawSVGPlugin from "gsap/DrawSVGPlugin"
import { ReactComponent as LineFourSVG } from "images/how-it-works/HowItWorksLineFour.svg"
import { ReactComponent as LineOneSVG } from "images/how-it-works/HowItWorksLineOne.svg"
import { ReactComponent as LineTwoSVG } from "images/how-it-works/HowItWorksLineTwo.svg"
import { ReactComponent as PricingConnectionsSVG } from "images/how-it-works/PricingConnections.svg"
import { ReactComponent as SuccessSVG } from "images/icons/success.svg"
import bigClosedBox from "images/illustrations/BigClosedBox.svg"
import longCargoBox from "images/illustrations/LongCargoBox.svg"
import openBox from "images/illustrations/OpenBox.svg"
import pallet from "images/illustrations/Pallet.svg"
import smallClosedBox from "images/illustrations/SmallClosedBox.svg"
import tape from "images/illustrations/Tape.svg"
import ConstantMarquee from "library/ConstantMarquee"
import { ScreenContext } from "library/ScreenContext"
import { svgColor } from "library/cssHelpers"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import { useContext, useRef } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"

gsap.registerPlugin(DrawSVGPlugin)

export default function Pricing() {
	const wrapperRef = useRef<HTMLDivElement | null>(null)
	const { mobile, tablet } = useContext(ScreenContext)

	return (
		<Wrapper ref={wrapperRef}>
			<Carousel timing={100}>
				<Illustration src={bigClosedBox} />
				<Illustration src={longCargoBox} />
				<Illustration src={openBox} />
				<Illustration src={pallet} />
				<Illustration src={smallClosedBox} />
				<Illustration src={tape} />
			</Carousel>
			<Box />
			<Pallet />
			<PanelBorder className="panel-border">
				{mobile || tablet ? (
					<PricingConnections />
				) : (
					<>
						<LineOne className="pricingActiveLine" />
						<LineTwo className="pricingActiveLine" />
						<LineFour className="pricingActiveLine" />
						<LineFive className="pricingActiveLine" />
					</>
				)}
				<Panel className="panel">
					<KickerIcon className="panel-kicker">
						<SuccessSVG />
					</KickerIcon>
					<Title>Start Saving.</Title>
					<Row>
						<CenteredButton to={links.pricing}>See Pricing</CenteredButton>
						<Description>
							Get up and running in days, not months, with Response&apos;s fast
							onboarding process and user-friendly interface.
						</Description>
					</Row>
				</Panel>
			</PanelBorder>
		</Wrapper>
	)
}

const Wrapper = styled.section`
	display: grid;
	place-items: center;
	position: relative;

	& > * {
		grid-area: 1 / 1 / 2 / 2;
	}

	${fresponsive(css`
		padding-bottom: 105px;
	`)}

	${fmobile(css`
		padding: 75px 0;
	`)}
`

const Carousel = styled(ConstantMarquee)`
	position: absolute;
	left: 50%;
	translate: -50vw 25px;
	z-index: 1;

	${fmobile(css`
		display: none;
	`)}
`

const Illustration = styled.img.attrs({ alt: "" })`
	${fresponsive(css`
		height: 501px;
		margin-right: 650px;
	`)}
`

const Box = styled.img.attrs({ alt: "", src: bigClosedBox })`
	display: none;

	${fmobile(css`
		display: block;
		position: absolute;
		z-index: 1;
		bottom: -89px;
		left: -57px;
		width: 244px;
		max-width: none;
		scale: 0.75;
	`)}
`

const Pallet = styled.img.attrs({ alt: "", src: pallet })`
	display: none;

	${fmobile(css`
		display: block;
		position: absolute;
		z-index: 1;
		top: -91px;
		right: -141px;
		width: 362px;
		max-width: none;
		scale: 0.75;
	`)}
`

const PanelBorder = styled.div`
	border: 1px solid ${colors.grey1};

	${fresponsive(css`
		display: grid;
		place-items: center;
		width: 980px;
		border-radius: 32px;
		padding: 10px;
		height: fit-content;
		position: relative;
		z-index: 2;
	`)}

	${fmobile(css`
		border: none;
		width: 100%;
		padding: 0;
	`)}
`

const Panel = styled.div`
	${fresponsive(css`
		width: 100%;
		display: grid;
		padding: 65px 150px 85px;
		gap: 30px;
		place-items: center;
		border-radius: 20px;
		border: 1px solid ${colors.grey1};
		background: ${colors.transAlpine1};
		backdrop-filter: blur(6px);
		position: relative;
		z-index: 2;
	`)}

	${fmobile(css`
		width: 345px;
		padding: 36px 0 45px;
		margin: 0;
		gap: 24px;
	`)}
`

const Title = styled.div`
	${fresponsive(css`
		${textStyles.h2}
		width: 655px;
		text-align: center;
		margin: -10px 0;
	`)}

	${fmobile(css`
		${textStyles.h4};
		width: 279px;
	`)}
`

const Row = styled.div`
	${fresponsive(css`
		display: flex;
		gap: 36px;
	`)}

	${fmobile(css`
		flex-direction: column-reverse;
		align-items: center;
	`)}
`

const CenteredButton = styled(PrimaryCTA)`
	${fmobile(css`
		width: fit-content;
		padding: 25px 45px;
	`)}
`

const Description = styled.div`
	${fresponsive(css`
		color: ${colors.grey2};
		padding-top: 3px;
		${textStyles.p1}
		width: 364px;
		text-align: left;
	`)}

	${fmobile(css`
		${textStyles.p2};
		width: 274px;
		text-align: center;
	`)}
`

const KickerIcon = styled.div`
	border: 1px solid ${colors.grey2};
	background: ${colors.transAlpine1};

	${fresponsive(css`
		display: grid;
		place-items: center;
		width: 40px;
		height: 40px;
		border-radius: 8px;
		${svgColor(colors.grey3)};

		svg {
			width: 13.5px;
			height: auto;
			margin-left: 2px;
		}
	`)}
`

const LineBase = css`
	position: absolute;
	transform: translateX(-50%);
`

const StraightLineBase = css`
	${LineBase};

	${fresponsive(css`
		width: 2px;
		height: 117px;
		top: -117px;
	`)}
`

const BentLineBase = css`
	${LineBase};

	${fresponsive(css`
		width: 8px;
		height: 90px;
		top: -90px;
	`)}
`

const LineOne = styled(LineOneSVG)`
	${BentLineBase};

	${fresponsive(css`
		left: 48.25%;
	`)}
`

const LineTwo = styled(LineTwoSVG)`
	${StraightLineBase};

	${fresponsive(css`
		left: 49.3%;
	`)}
`

const LineFour = styled(LineTwoSVG)`
	${StraightLineBase};

	${fresponsive(css`
		left: 50.75%;
	`)}
`

const LineFive = styled(LineFourSVG)`
	${BentLineBase};

	${fresponsive(css`
		left: 51.75%;
	`)}
`

const PricingConnections = styled(PricingConnectionsSVG)`
	${fresponsive(css`
		display: none;
		position: absolute;
		width: 40px;
		height: 117px;
		z-index: 2;
	`)}

	${ftablet(css`
		display: block;
		left: 50%;
		transform: translateX(-50%);
		top: -115px;
	`)}

	${fmobile(css`
		display: block;
		top: -114px;
		left: 37px;
	`)}
`
